import React, { useEffect, useState } from 'react';
import { SbEditableContent } from 'storyblok-react';
import { blokToComponent } from '../helpers';
import { Props } from '../types';
import { ContentService, StoryblokService } from '../../services';

interface UpcomingContent extends SbEditableContent {
  fallback: Array<SbEditableContent>;
  story: {
    id: string;
    story: {
      id: number;
    };
  };
  storyId: string;
}

// eslint-disable-next-line max-len
const useStory = (storyToLoadUuid: string, storyToLoadId: number, currentStoryId: string | number) => {
  const [story, setStory] = useState(null);
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(true);

  if (!storyToLoadUuid) {
    return {
      isFetching: false,
      error: new Error('Please select a story using the component\'s story field.'),
    };
  }

  if (currentStoryId === storyToLoadId) {
    return {
      isFetching: false,
      error: new Error('Upcoming content must never load the same page/story its placed in. Please pick another page which is not the same you are editing right now..'),
    };
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Note that upcoming-content can never fetch draft/preview content.
        const response = await new ContentService().getStory(storyToLoadUuid, { cache: 'no-store' }, 'live');
        setStory(response?.data?.story);
      } catch (errorState) {
        setError(errorState);
      }
    };

    fetchData()
      .then(() => setIsFetching(false));
  }, [storyToLoadUuid]);

  return { story, isFetching, error };
};

const renderError = (error: Record<string, string>): JSX.Element => {
  if (!error || !StoryblokService.isInEditor()) {
    return null;
  }

  return (
    <roche-grid style={{ color: 'red' }}>
      <h2>Failed to load upcoming content.</h2>
      <p>{error.message}</p>
    </roche-grid>
  );
};

export const UpcomingContent = (
  { blok, getComponent, storyId: currentStoryId }
  : Props<UpcomingContent>,
): JSX.Element => {
  const { fallback, story: storyPointer } = blok;

  const storyToLoadId = storyPointer?.story?.id;

  const { story, isFetching, error } = useStory(storyPointer.id, storyToLoadId, currentStoryId);

  if (isFetching) {
    return <roche-loading-spinner></roche-loading-spinner>;
  }

  if (!story) {
    return <>
      {renderError(error)}
      {fallback?.map((component, index: number) => blokToComponent({ blok: { ...component }, getComponent }, `${index}`)) ?? null}
    </>;
  }

  return blokToComponent({ blok: story.content, getComponent, storyId: story.id });
};
